export type FieldType =
  | "text" | "textarea" | "number" | "date" | "boolean"
  | "select" | "multiselect" | "email" | "url" | "phone";

export type ValidateResult =
  | { ok: true; value: unknown }
  | { ok: false; error: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE    = /^[+0-9 ()\-.]+$/;
const fail = (msg: string): ValidateResult => ({ ok: false, error: msg });

export function validateFieldValue(
  type: FieldType, raw: unknown, options?: string[],
): ValidateResult {
  // null is always permitted (a cleared value); is_required is checked at the form layer.
  if (raw === null || raw === undefined || raw === "") {
    if (type === "number" || type === "boolean" || type === "select" || type === "multiselect" ||
        type === "text" || type === "textarea" || type === "email" || type === "url" ||
        type === "phone" || type === "date") {
      return { ok: true, value: null };
    }
  }
  switch (type) {
    case "text": case "textarea":
      return typeof raw === "string" ? { ok: true, value: raw } : fail("must be a string");
    case "email":
      return typeof raw === "string" && EMAIL.test(raw) ? { ok: true, value: raw } : fail("invalid email");
    case "url":
      try { if (typeof raw !== "string") return fail("must be a string");
            new URL(raw); return { ok: true, value: raw }; }
      catch { return fail("invalid url"); }
    case "phone":
      return typeof raw === "string" && PHONE.test(raw) ? { ok: true, value: raw } : fail("invalid phone");
    case "number": {
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(n) ? { ok: true, value: n } : fail("not a number");
    }
    case "boolean":
      return typeof raw === "boolean" ? { ok: true, value: raw } : fail("must be boolean");
    case "date":
      return typeof raw === "string" && ISO_DATE.test(raw) ? { ok: true, value: raw } : fail("date must be YYYY-MM-DD");
    case "select": {
      if (!Array.isArray(options) || options.length === 0) return fail("no options configured");
      return typeof raw === "string" && options.includes(raw) ? { ok: true, value: raw } : fail("not in options");
    }
    case "multiselect": {
      if (!Array.isArray(options) || options.length === 0) return fail("no options configured");
      if (!Array.isArray(raw)) return fail("must be an array");
      const seen = new Set<string>();
      for (const v of raw) {
        if (typeof v !== "string" || !options.includes(v)) return fail("not in options");
        seen.add(v);
      }
      // dedupe + preserve first-seen order
      const out = (raw as string[]).filter((v, i, a) => a.indexOf(v) === i);
      return { ok: true, value: out };
    }
  }
}
